import React, { useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import { FaChevronLeft, FaChevronRight, FaLayerGroup } from "react-icons/fa";
import { useHomeContext } from "../../../context/Home_context";
import DOMPurify from "dompurify";
import noImg from "../../../Assets/No_img.jpg";

import "swiper/css";
import "./HomeFocusSector.css";

const HomeFocusSector = () => {
  const { current_expo_data } = useHomeContext();
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const swiperRef = useRef(null);

  const focusSectors = current_expo_data?.focussectors || [];

  return (
    <section className="fs-section" id="focus-sectors">
      {/* Background Ambient Glowing Orbs */}
      <div className="fs-bg-glow fs-glow-saffron"></div>
      <div className="fs-bg-glow fs-glow-blue"></div>

      {/* Blueprint Grid Lines */}
      <div className="fs-blueprint-grid"></div>

      <div className="fs-container">
        {/* Section Header */}
        <div className="fs-header">
          <h2 className="fs-title">
            FOCUS <span className="fs-title-highlight">SECTORS</span>
          </h2>
          {current_expo_data?.focus_sector_description && (
            <div
              className="fs-subtitle"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  current_expo_data.focus_sector_description,
                ),
              }}
            />
          )}
        </div>

        {/* Outer Swiper Wrapper */}
        <div className="fs-outer-swiper-wrapper">
          {focusSectors.length > 5 && (
            <button
              className="fs-outer-nav-btn fs-outer-prev"
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label="Previous Slide"
            >
              <FaChevronLeft />
            </button>
          )}

          <div className="fs-swiper-container">
            {focusSectors.length === 0 ? (
              <div className="fs-no-data">
                <div className="fs-no-data-icon-wrapper">
                  <FaLayerGroup className="fs-no-data-icon" />
                </div>
                <p className="fs-no-data-text">No Focus Sectors Available</p>
              </div>
            ) : (
              <Swiper
                onBeforeInit={(swiper) => {
                  swiperRef.current = swiper;
                }}
                modules={[Autoplay]}
                spaceBetween={22}
                slidesPerView={1}
                centerInsufficientSlides={true}
                loop={focusSectors.length > 5}
                autoplay={
                  focusSectors.length > 5
                    ? {
                        delay: 2500,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                      }
                    : false
                }
                breakpoints={{
                  480: {
                    slidesPerView: 2,
                    spaceBetween: 16,
                  },
                  768: {
                    slidesPerView: 3,
                    spaceBetween: 20,
                  },
                  1024: {
                    slidesPerView: 4,
                    spaceBetween: 20,
                  },
                  1280: {
                    slidesPerView: 5,
                    spaceBetween: 22,
                  },
                }}
                className="fs-swiper"
              >
                {focusSectors.map((sector, index) => {
                  const imageSrc = sector.image_full_path || sector.image;

                  return (
                    <SwiperSlide
                      key={sector.id || index}
                      className="fs-swiper-slide"
                    >
                      <div className="fs-card-wrapper">
                        <div
                          className="fs-card"
                          onClick={() => setLightboxIndex(index)}
                        >
                          {/* Card Image Area with CSS Background Shimmer */}
                          <div className="fs-card-image-wrapper">
                            <img
                              src={imageSrc || noImg}
                              alt={sector.name}
                              className="fs-card-image"
                              loading="lazy"
                              onError={(e) => {
                                e.target.src = noImg;
                              }}
                            />
                          </div>

                          {/* Card Bottom Area */}
                          <div className="fs-card-content">
                            <h3 className="fs-card-title">{sector.name}</h3>
                          </div>

                          {/* Hover Accent Bar */}
                          <div className="fs-card-glow-bar"></div>
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            )}
          </div>

          {focusSectors.length > 5 && (
            <button
              className="fs-outer-nav-btn fs-outer-next"
              onClick={() => swiperRef.current?.slideNext()}
              aria-label="Next Slide"
            >
              <FaChevronRight />
            </button>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      <Lightbox
        open={lightboxIndex >= 0}
        index={lightboxIndex}
        close={() => setLightboxIndex(-1)}
        slides={focusSectors.map((sector) => ({
          src: sector.image_full_path || sector.image,
          alt: sector.name,
        }))}
        plugins={[Zoom]}
        zoom={{
          maxZoomPixelRatio: 5,
          zoomInMultiplier: 2,
        }}
      />
    </section>
  );
};

export default HomeFocusSector;
